import type { BenchmarkKeytermMode, KeytermSource } from "./types";

export interface CellReviewKeytermModeOption {
  value: "baseline" | "keywords" | "prompt" | "combined";
  label: string;
}

export function cellReviewKeytermModeOptions(
  source: KeytermSource,
): CellReviewKeytermModeOption[] {
  if (source === "slide_context") {
    return [
      { value: "baseline", label: "Baseline only (no hints)" },
      { value: "keywords", label: "Keywords only" },
      { value: "prompt", label: "Prompt only" },
      { value: "combined", label: "Prompt + keywords" },
    ];
  }
  return [
    { value: "baseline", label: "Baseline only (no hints)" },
    { value: "keywords", label: "Keyterms on" },
  ];
}

/**
 * Identity of a run's cell population, for the cell-review rows request.
 *
 * Rows are written as a run progresses, so a page fetched seconds after the run
 * started is not the same page as the one available once it completes. Including
 * this in the request identity makes the rows reload when the population
 * changes; without it, a run opened while queued or running keeps its empty
 * first page after completion.
 */
export function cellReviewRunFingerprint(
  status: string | undefined,
  completedCells: number | undefined,
): string {
  if (!status) return "";
  return `${status}/${completedCells ?? 0}`;
}

export function isCellReviewBaseline(mode: BenchmarkKeytermMode): boolean {
  return mode === "off" || mode === "baseline";
}

export function cellReviewVariantLabel(mode: BenchmarkKeytermMode): string {
  switch (mode) {
    case "off":
    case "baseline":
      return "Unprompted baseline";
    case "on":
      return "Keyterms treatment";
    case "keywords":
      return "Keywords treatment";
    case "prompt":
      return "Prompt treatment";
    case "combined":
      return "Prompt + keywords treatment";
  }
}

export function cellReviewTrackLabel(
  source: KeytermSource,
  mode: BenchmarkKeytermMode,
): string {
  if (isCellReviewBaseline(mode)) return "Control · no hints sent";
  if (source === "slide_context") return "Published slide data · treatment";
  if (source === "reference_derived") return "Reference-derived · treatment";
  return "Reviewed terms · treatment";
}

export function resolveCellReviewKeytermMode(
  source: KeytermSource,
  filter: string,
): BenchmarkKeytermMode | undefined {
  if (!filter) return undefined;
  if (source === "slide_context") {
    return ["baseline", "keywords", "prompt", "combined"].includes(filter)
      ? filter as BenchmarkKeytermMode
      : undefined;
  }
  if (filter === "baseline") return "off";
  if (filter === "keywords") return "on";
  return undefined;
}
